import axios from 'axios';
import debounce from 'lodash/debounce';
import padStart from 'lodash/padStart';
import {getFromTemplate, triggerEvent} from '../base/utils';
import ExhibitsData from './exhibits-data';

class TicketAvailability {
    constructor($container) {
        this.$container = $container;

        if (!$container) {
            return;
        }

        this.url = this.$container.dataset.url;
        this.exhibitsUrl = this.$container.dataset.exhibits;
        this.lang = this.$container.dataset.lang;

        if (!this.exhibitsUrl || !this.url) {
            return;
        }

        try {
            this.translations = window.TRANSLATIONS.calendar;
            this.holidays = JSON.parse(this.$container.dataset.holidays);
        } catch (e) {
            console.error('Malformed JSON');
        }

        this.$days = this.$container.querySelector('.js-days');
        this.$date = this.$container.querySelector('.js-date');
        this.$dateLabel = this.$container.querySelector('.js-date-label');
        this.$categoryLabel = this.$container.querySelector('.js-category-label');
        this.$month = this.$container.querySelector('.js-month');
        this.$monthName = this.$container.querySelector('.js-month-name');
        this.$monthNext = this.$container.querySelector('.js-month-next');
        this.$monthPrev = this.$container.querySelector('.js-month-prev');
        this.$categoryTabs = this.$container.querySelector('.js-category-tabs');
        this.$categorySelect = this.$container.querySelector('.js-category-select');
        this.$results = this.$container.querySelector('.js-results');
        this.$eventTemplate = this.$container.querySelector('.js-availability-result');
        this.$emptyTemplate = this.$container.querySelector('.js-availability-empty');
        this.$closedTemplate = this.$container.querySelector('.js-availability-closed');

        if (!this.$days || !this.$date || !this.$month || !this.$results) {
            return;
        }

        this.init();

        window.addEventListener('resize', debounce(this.resize.bind(this), 300));
    }

    init() {
        this.eventTemplate = this.$eventTemplate.innerHTML;
        this.emptyTemplate = this.$emptyTemplate.innerHTML;
        this.closedTemplate = this.$closedTemplate.innerHTML;

        setTimeout(async () => {
            try {
                this.exhibits = await ExhibitsData.get(this.exhibitsUrl);
                this.initDate();
            } catch (error) {
                console.warn(error);
            }
        }, 0);
    }

    load() {
        this.buildCalendar();
    }

    initCategory() {
        this.resize();

        for (let $el of this.$categoryTabsOptions) {
            $el.addEventListener('change', this.changeCategoryTab.bind(this));
        }
        this.$categorySelect.addEventListener('change', this.changeCategory.bind(this));
    }

    changeCategoryTab(e) {
        for (let $el of this.$categoryTabsOptions) {
            $el.setAttribute('tabindex', '-1');
            $el.setAttribute('aria-selected', 'false');
        }
        e.target.setAttribute('tabindex', '0');
        e.target.setAttribute('aria-selected', 'true');
        this.category = e.target.value;
        this.$categoryLabel.innerText = e.target.dataset.title;
        this.renderEvents();
    }

    changeCategory() {
        this.category = this.$categorySelect.value;
        this.renderEvents();
    }

    resize() {
        if (document.documentElement.clientWidth < 1024) {
            if (this.$categoryTabsOptions) {
                for (let $el of this.$categoryTabsOptions) {
                    $el.setAttribute('disabled', 'disabled');
                }
            }
            this.$categorySelect.removeAttribute('disabled');
            // this.$categorySelect.value = this.category;
            triggerEvent(this.$categorySelect, 'select', this.category);
        } else {
            if (this.$categoryTabsOptions) {
                for (let $el of this.$categoryTabsOptions) {
                    $el.removeAttribute('disabled');
                }
                this.$categoryTabs.querySelector(`[value="${this.category}"]`).setAttribute('checked', 'checked');
            }
            this.$categorySelect.setAttribute('disabled', 'disabled');
        }
    }

    initDate() {
        let dateNow = new Date();

        this.year = dateNow.getFullYear();
        this.month = dateNow.getMonth() + 1;

        this.setDate();

        this.current = `${this.yearMonthPadded}-${padStart(dateNow.getDate(), 2, '0')}`;
    }

    setDate() {
        let monthToSet = this.month === 0 ? 12 : this.month;

        this.dateStart = new Date(this.year, monthToSet - 1, 1);
        this.dateEnd = new Date((new Date(this.year, monthToSet, 1)).setDate(0));
        this.yearMonth = `${this.year}-${monthToSet}`;
        this.yearMonthPadded = `${this.year}-${padStart(monthToSet, 2, '0')}`;
    }

    buildCalendar() {
        this.showMonth();

        this.$monthNext.addEventListener('click', this.nextMonth.bind(this));
        this.$monthPrev.addEventListener('click', this.prevMonth.bind(this));
    }

    changeDay(e) {
        this.$days.querySelector('.selected').classList.remove('selected');
        e.target.classList.add('selected');
        this.current = e.target.dataset.date;
        this.$date.innerHTML = e.target.title;
        this.$dateLabel.innerText = e.target.title;
        this.renderEvents();
    }

    prevMonth() {
        if (this.month === 1) {
            this.year--;
            this.month = 0;
        } else if (this.month === 0) {
            this.month = 11;
        } else {
            this.month = (this.month - 1) % 12;
        }

        this.setDate();
        this.current = `${this.yearMonthPadded}-${this.dateEnd.getDate()}`;

        this.showMonth();

        if (this.dateStart <= Date.now()) {
            this.$monthPrev.setAttribute('disabled', 'disabled');
        }
    }

    nextMonth() {
        this.month = (this.month + 1) % 12;
        if (this.month === 1) this.year++;

        this.setDate();
        this.current = `${this.yearMonthPadded}-01`;

        this.showMonth();

        if (this.dateStart > Date.now()) {
            this.$monthPrev.removeAttribute('disabled');
        }
    }

    showMonth() {
        let firstWeekday = this.dateStart.getDay(),
            lastDay = this.dateEnd.getDate();

        this.$results.classList.add('loader');
        this.renderCalendar(firstWeekday, lastDay);
        this.getEvents(`${this.yearMonth}-01`, `${this.yearMonth}-${lastDay}`);
    }

    renderCalendar(firstWeekday, lastDay) {
        let html = '',
            monthName = this.translations.monthNames[this.month === 12 ? 0 : this.month];

        let firstWeekdayPrev  = (firstWeekday == 0)? 7 : firstWeekday;
        for (let d = firstWeekdayPrev - 1; d > 0; d--) {
            html += '<li class="day-previous-month"></li>';
        }

        for (let d = 1, weekday = firstWeekday; d <= lastDay; d++) {
            let day = padStart(d, 2, '0'),
                isSelected = `${this.yearMonthPadded}-${day}` === this.current,
                className = isSelected ? 'selected' : '',
                tabindex = isSelected ? '' : 'tabindex="-1"',
                date = `${this.yearMonthPadded}-${day}`,
                label = `${this.translations.weekdayNames[weekday]}, ${d} ${monthName}`;

            html += `<li>
                    <div>${this.translations.weekdays[weekday]}</div>
                    <button class="day ${className}" ${tabindex} data-date="${date}" aria-label="${label}" title="${label}" aria-selected="${isSelected}" aria-controls="availability-results" role="tab">${d}</button>
                </li>`;
            weekday = (weekday + 1) % 7;

            if (isSelected) {
                this.$date.innerHTML = `${d} ${monthName}`;
                this.$dateLabel.innerText = `${label}`;
            }
        }

        this.$days.innerHTML = '';
        this.$days.insertAdjacentHTML('beforeend', html);

        this.$monthName.innerHTML = monthName;

        for (let $el of this.$days.querySelectorAll('.day')) {
            $el.addEventListener('click', this.changeDay.bind(this));
            $el.addEventListener('keydown', this.navigateDays.bind(this));
        }
    }

    navigateDays(e) {
        let $curr = e.target,
            $li = $curr.closest('li'),
            $newLi, $newCurr;

        switch (e.keyCode) {
            case 37: $newLi = $li.previousElementSibling; break; // left
            case 39: $newLi = $li.nextElementSibling; break; // right
        }

        if (!$newLi) {
            return;
        }

        $newCurr = $newLi.querySelector('.day');

        if (!$newCurr) {
            return;
        }

        $newCurr.setAttribute('tabindex', '0');
        $newCurr.focus();
        $newCurr.click();
        $curr.setAttribute('tabindex', '-1');
    }

    getEvents(dateFrom, dateTo) {
        axios.get(`${this.url}?dateFrom=${dateFrom}&dateTo=${dateTo}&lang=${this.lang}`)
            .then(data => {
                this.$results.classList.remove('loader');
                if (data.status === 200) {
                    this.data = data.data;
                    if (!this.categories) {
                        this.renderCategories();
                    }
                    this.renderEvents();
                }
            })
            .catch((error) => {
                console.warn(error);
                this.$results.innerHTML = '';
                this.$results.classList.remove('loader');
                // this.$results.appendChild(this.getFromTemplate(this.errorTemplate));
            });
    }

    renderCategories() {
        let tabs = '',
            options = [];

        this.categories = this.data['event-collections'];

        for (let c in this.categories) {
            if (this.categories.hasOwnProperty(c)) {
                if (c === 'label_cnk_attraction_event_collection_entrance_group') continue; // #141029 - wyłączenie Wystaw dla grup; Wieczory dla dorosłych przywrócone w #149046

                let name = this.categories[c];

                tabs += `<li class="tabs-list-item" role="presentation">
                    <input type="radio" name="category" value="${c}" id="${c}" role="tab" aria-controls="availability-results" data-title="${name}" ${this.category ? 'tabindex="-1" aria-selected="false"' : 'aria-selected="true"' } />
                    <label class="tabs-tab" for="${c}">${name}</label>
                </li>`;

                // options += `<option value="${c}" >${name}</option>`;
                options.push({value: c, label: name});

                if (!this.category) {
                    this.category = c;
                    this.$categoryLabel.innerText = name;
                }
            }
        }

        triggerEvent(this.$categorySelect, 'set', options);

        this.$categoryTabs.insertAdjacentHTML('beforeend', tabs);
        // this.$categorySelect.insertAdjacentHTML('beforeend', options);
        this.$categoryTabsOptions = this.$categoryTabs.querySelectorAll('input');

        this.initCategory();
    }

    renderEvents() {
        let dayEvents = this.data[this.current],
            events = dayEvents ? dayEvents[this.category] : null;

        this.$results.innerHTML = '';

        let holidayMsg = this.isHoliday(this.current, this.category);
        if (holidayMsg) {
            this.$results.innerHTML = `<div class="availability-results-empty">${holidayMsg}</div>`;
            return;
        }

        if (!dayEvents) {
            this.$results.innerHTML = '<div class="availability-results-empty">' + this.closedTemplate + '</div>';
            return;
        }

        if (!events) {
            this.$results.innerHTML = '<div class="availability-results-empty">' + this.emptyTemplate + '</div>';
            return;
        }

        let availableHours = [];

        for (let slug in events) {
            if (events.hasOwnProperty(slug)) {
                let event = events[slug];

                for (let hour in event.availability) {
                    if (event.availability.hasOwnProperty(hour)) {
                        let availableHour = event.availability[hour];

                        availableHour.slug = slug;
                        availableHour.hour = hour;

                        availableHours.push(availableHour);
                    }
                }
            }
        }

        availableHours.sort((a, b) => a.hour.localeCompare(b.hour)).forEach((hour) => {
            this.$results.appendChild(this.renderEvent(hour.slug, hour.hour, hour.quantity, this.category));
        });
    }

    renderEvent(slug, hour, quantity, category) {
        let itemHtml = this.eventTemplate,
            eventData = this.exhibits[slug],
            name = '',
            $item;

        itemHtml = itemHtml.replace(`[hour]`, hour);
        itemHtml = itemHtml.replace(`[quantity]`, quantity);

        if (eventData) {
            if (eventData.published === 'true' && eventData.url) {
                name = `<a href="${eventData.url}" class="link" target="_blank" rel="noopener"><span class="underline">${eventData.title}</span></a>`;
            } else {
                name = eventData.title;
            }
        }
        itemHtml = itemHtml.replace(`[name]`, name);
        itemHtml = itemHtml.replace(/\[label]/gi, this.translations.labels[quantity]);

        $item = getFromTemplate(itemHtml);

        if (category === 'label_cnk_attraction_event_collection_planetarium') {
            $item.classList.add('availability-result-pla');
        } else if (category === 'label_cnk_attraction_event_collection_laboratory_group') {
            $item.classList.add('availability-result-lab');
        } else {
            $item.querySelector('.name').remove();
        }

        return $item;
    }

    isHoliday(date, category) {
        if (this.holidays.length === 0) return false;

        let i = 0,
            n = this.holidays.length,
            isHoliday = false;

        while (!isHoliday && i < n) {
            let holiday = this.holidays[i];

            isHoliday = (category === holiday.category && date >= holiday.from && date <= holiday.to) ? holiday.message : false;
            i++;
        }

        return isHoliday;
    }
}

export default TicketAvailability;
